import { useState, useCallback } from 'react';
import { generatePersonas } from '../services/api';

interface GeneratePersonasParams {
  projectId: string;
  panelType: 'brand' | 'category' | 'b2b';
  count: number;
  medianHHI?: number;
}

interface UsePersonaGenerationReturn {
  generate: (params: GeneratePersonasParams) => Promise<void>;
  loading: boolean;
  progress: number;
  error: Error | null;
  success: boolean;
}

/**
 * Custom hook to handle persona generation
 * Manages loading state and error handling for the generation process
 */
export const usePersonaGeneration = (): UsePersonaGenerationReturn => {
  const [loading, setLoading] = useState<boolean>(false);
  const [progress, setProgress] = useState<number>(0);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState<boolean>(false);

  const generate = useCallback(async (params: GeneratePersonasParams) => {
    setLoading(true);
    setProgress(0);
    setError(null);
    setSuccess(false);

    try {
      // Simulate progress during generation
      const progressInterval = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 90) return prev;
          return prev + Math.random() * 10;
        });
      }, 2000);

      // Call the API to generate personas
      await generatePersonas(
        params.projectId,
        params.panelType,
        params.count,
        params.medianHHI
      );

      clearInterval(progressInterval);
      setProgress(100);
      setSuccess(true);
      setLoading(false);
    } catch (err) {
      console.error('Error generating personas:', err);
      setError(err instanceof Error ? err : new Error('Failed to generate personas'));
      setLoading(false);
      setProgress(0);
      setSuccess(false);
    }
  }, []);

  return {
    generate,
    loading,
    progress,
    error,
    success,
  };
};
